import * as React from 'react';
import { toast } from 'react-toastify';
import { AppState } from 'src';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { Action, Dispatch } from 'redux';
import { HostedTransactionState } from 'src/transaction/transaction.reducer';
import { HostedCheckoutTransactionViewModel } from '@models/HostedCheckoutTransactionViewModel';
import { AppDispatcher } from 'src/_models/common.models';
import { requestAction } from '@common/components/common.actions';
import { accountService } from 'src/_services/AccountService';
import { paymentService } from '@services/PaymentService';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum TransactionActionType {
  GetTransactionDataStart = 'GetTransactionDataStart',
  GetTransactionDataSuccess = 'GetTransactionDataSuccess',
  GetTransactionDataFailure = 'GetTransactionDataFailure',

  SetCopyCardToAccountLoading = 'CopyCardToAccountLoading',
  CopyCardToAccountSuccess = 'CopyCardToAccountSuccess',
  CancelHostedTransaction = 'CancelHostedTransaction',
  SetTransactionLandingPageLoading = 'SetTransactionLandingPageLoading',
  ResetTransactionState = 'ResetTransactionState'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Search **********/
export interface GetTransactionDataStartAction extends Action {
  readonly transactionId: number;
  readonly type: TransactionActionType.GetTransactionDataStart;
}

export interface GetTransactionDataSuccessAction extends Action {
  readonly result: HostedCheckoutTransactionViewModel;
}

export interface GetTransactionDataFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: TransactionActionType.GetTransactionDataFailure;
}

export interface CopyCardToAccountSuccessAction extends Action {
  readonly type: TransactionActionType.CopyCardToAccountSuccess;
}

export interface CopyCardToAccountLoadingAction extends Action {
  readonly type: TransactionActionType.SetCopyCardToAccountLoading;
  readonly isLoading: boolean;
}

export interface CancelHostedTransactionAction extends Action {
  readonly type: TransactionActionType.CancelHostedTransaction;
}

export interface ResetTransactionState extends Action {
  readonly type: TransactionActionType.ResetTransactionState;
}

export interface SetTransactionLandingPageLoadingAction extends Action {
  readonly type: TransactionActionType.SetTransactionLandingPageLoading;
  readonly isLoading: boolean;
}

/****** RESET STATE ********/
export function resetTransactionStore() {
  return {
    type: TransactionActionType.ResetTransactionState
  };
}

export function setTransactionLandingPageLoading(loading: boolean) {
  return {
    type: TransactionActionType.SetTransactionLandingPageLoading,
    isLoading: loading
  };
}

/********** Search **********/
export const getHostedTransaction = (transactionId: number) => {
  return async (dispatch: Dispatch<HostedTransactionState>) => {
    dispatch(getHostedTransactionStart(transactionId));
    let result: HostedCheckoutTransactionViewModel;
    try {
      result = await paymentService.getHostedTransaction(transactionId);
      return dispatch(getHostedTransactionSuccess(result));
    } catch (e) {
      dispatch(getHostedTransactionFailure(e.toString()));
      return null;
    }
  };
};

export function getHostedTransactionStart(transactionId: number): GetTransactionDataStartAction {
  return {
    transactionId: transactionId,
    type: TransactionActionType.GetTransactionDataStart
  };
}

export function cancelHostedTransaction(transactionId: number) {
  return async (dispatch: AppDispatcher) => {
    return dispatch(
      requestAction(
        () => paymentService.cancelTransaction(transactionId),
        d => {
          return { type: TransactionActionType.CancelHostedTransaction };
        },
        { hasToast: false }
      )
    );
  };
}

export function getHostedTransactionSuccess(
  result: HostedCheckoutTransactionViewModel
): GetTransactionDataSuccessAction {
  return {
    result: result,
    type: TransactionActionType.GetTransactionDataSuccess
  };
}

export function setTransactionCardCopyLoading(loading: boolean): CopyCardToAccountLoadingAction {
  return {
    type: TransactionActionType.SetCopyCardToAccountLoading,
    isLoading: loading
  };
}

export function transactionCardCopySuccess() {
  return {
    type: TransactionActionType.CopyCardToAccountSuccess
  };
}

export function saveTransactionCardToAccount(transactionId: number, accountId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const state = getState();
    const account = state.contract.payment.billableEntities.find(x => x.id === +accountId);
    if (!account) {
      toast.error(
        <ToastComponent type={ToastType.Error} message="Unable to locate account data, Card data not saved." />,
        ToastOptions.Error
      );
      return;
    }
    return dispatch(
      requestAction(
        () => accountService.saveCardFromTransactionToAccount(transactionId, account),
        transactionCardCopySuccess,
        {
          hasToast: true,
          toastSuccessMessage: 'Card saved successfully.',
          overrideFailMessage: 'Unable to save card data. Try again later.',
          loadingAction: setTransactionCardCopyLoading
        }
      )
    );
  };
}

export function getHostedTransactionFailure(errorMessage: string): GetTransactionDataFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to load transaction data. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: TransactionActionType.GetTransactionDataFailure
  };
}



// WEBPACK FOOTER //
// ./src/transaction/transaction.actions.tsx